import { validAnalysisUsage, type MarketAnalysisUsage } from './analysisUsage';
import { productAttributes } from './productAttributes';
import { validReportConfig, type MarketReportConfig } from './reportConfig';

/** Shared, persisted analysis. These read contracts never start a model or collection. */
export interface MarketInsightState {
  status:
    | "not_started"
    | "preparing"
    | "queued"
    | "running"
    | "ready"
    | "failed"
    | "unavailable";
  analysis_version?: string;
  error_code?: string | null;
  created_at?: string;
  window_start?: string;
  window_end?: string;
  consumption?: MarketAnalysisUsage;
  /** Frozen per evidence version, not the monitor's current editable schedule. */
  report_config?: MarketReportConfig;
  report_due_at?: string | null;
}

export interface MarketInsightDocument extends MarketInsightState {
  analysis_version: string;
  generated_at: string;
  input_digest: string;
  facts: {
    status: string;
    eligible_evidence: number;
    analyzed_evidence: number;
    full_text_evidence: number;
    unknowns: string[];
  };
  output: {
    /** v3 adds business framing; old persisted documents remain readable. */
    summary?: string;
    findings: {
      title: string;
      interpretation: string;
      business_impact?: string;
      suggested_action?: string;
      evidence_ids: string[];
    }[];
    limitations: string[];
  };
}

export interface MarketInsights {
  monitor_name?: string;
  landing_version?: string | null;
  media_inspection_version?: string | null;
  creative_version?: string | null;
  media_version?: string | null;
  monitor_id: string;
  content_trust: "external_untrusted";
  model_enabled: boolean;
  stale: boolean;
  update: MarketInsightState;
  insight: MarketInsightDocument | null;
}

export interface MarketInsightEvidencePage {
  analysis_version: string;
  content_trust: "external_untrusted";
  items: {
    evidence_id: string;
    source_system: string;
    source_url: string | null;
    data: Record<string, unknown>;
  }[];
  next_cursor: string | null;
}

const object = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === "object" && !Array.isArray(value);
const strings = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item) => typeof item === "string");
const states = new Set([
    "not_started",
    "preparing",
    "queued",
    "running",
    "ready",
    "failed",
    "unavailable",
]);
const state = (value: unknown): boolean =>
    object(value) && states.has(String(value.status))
    && validVersionSchedule(value)
    && (value.consumption === undefined || validAnalysisUsage(value.consumption, value.analysis_version));

const validVersionSchedule = (value: Record<string, unknown>): boolean => {
    if (value.report_config === undefined) return value.report_due_at === undefined;
    if (!validReportConfig(value.report_config)) return false;
    if ((value.report_config as MarketReportConfig).cadence === 'per_update') return value.report_due_at === null;
    return typeof value.report_due_at === 'string'
        && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value.report_due_at)
        && Number.isFinite(Date.parse(value.report_due_at));
};

export function validateInsights(value: unknown): MarketInsights {
    if (
        !object(value)
    || typeof value.monitor_id !== "string"
    || (value.monitor_name !== undefined && (typeof value.monitor_name !== 'string' || !value.monitor_name.trim() || value.monitor_name.length > 500))
    || value.content_trust !== "external_untrusted"
    || typeof value.model_enabled !== "boolean"
    || typeof value.stale !== "boolean"
    || (value.landing_version !== undefined && value.landing_version !== null
        && (typeof value.landing_version !== 'string' || !/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value.landing_version)))
    || (value.media_inspection_version !== undefined && value.media_inspection_version !== null
        && (typeof value.media_inspection_version !== 'string' || !/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value.media_inspection_version)))
    || (value.media_version !== undefined && value.media_version !== null && typeof value.media_version !== 'string')
    || (value.creative_version !== undefined && value.creative_version !== null
        && (typeof value.creative_version !== 'string' || !/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value.creative_version)))
    || !state(value.update)
    )
        throw new Error("Invalid stored insight response");
    const report = value.insight;
    if (
        report !== null
    && (!object(report)
      || report.status !== "ready"
      || !state(report)
      || typeof report.analysis_version !== "string"
      || typeof report.generated_at !== "string"
      || typeof report.input_digest !== "string"
      || !object(report.facts)
      || !Number.isInteger(report.facts.eligible_evidence)
      || !Number.isInteger(report.facts.analyzed_evidence)
      || !Number.isInteger(report.facts.full_text_evidence)
      || typeof report.facts.status !== "string"
      || !strings(report.facts.unknowns)
      || !object(report.output)
      || (report.output.summary !== undefined && typeof report.output.summary !== "string")
      || !strings(report.output.limitations)
      || !Array.isArray(report.output.findings)
      || !report.output.findings.every(
          (finding) =>
              object(finding)
          && typeof finding.title === "string"
          && typeof finding.interpretation === "string"
          && (finding.business_impact === undefined || typeof finding.business_impact === "string")
          && (finding.suggested_action === undefined || typeof finding.suggested_action === "string")
          && strings(finding.evidence_ids)
          && finding.evidence_ids.length > 0,
      ))
    )
        throw new Error("Invalid stored insight document");
    return value as unknown as MarketInsights;
}

export function validateInsightEvidence(
    value: unknown,
    expected?: { version: string; evidenceIds?: string[] },
): MarketInsightEvidencePage {
    if (
        !object(value)
    || typeof value.analysis_version !== "string"
    || value.content_trust !== "external_untrusted"
    || (value.next_cursor !== null
      && (typeof value.next_cursor !== "string"
        || !/^\d+$/.test(value.next_cursor)))
    || !Array.isArray(value.items)
    || !value.items.every(
        (item) =>
            object(item)
        && typeof item.evidence_id === "string"
        && typeof item.source_system === "string"
        && (item.source_url === null || typeof item.source_url === "string")
        && object(item.data),
    )
    )
        throw new Error("Invalid stored insight evidence");
    const page = value as unknown as MarketInsightEvidencePage;
    page.items.forEach(item => { if (item.source_system === 'amazon') productAttributes(item.data); });
    if (expected && (page.analysis_version !== expected.version
        || (expected.evidenceIds && page.items.some(item => !expected.evidenceIds!.includes(item.evidence_id)))))
        throw new Error("Stored insight evidence selection mismatch");
    return page;
}
